import { motion } from 'motion/react';

import { BottomSheet } from '@common/ui/BottomSheet';
import { CachedImage } from '@common/ui/CachedImage';

import styles from './SuperlikeOverlay.module.scss';

export type SuperlikeOverlayProps = {
  testId: string;
  /** The dialog's accessible name. */
  label: string;
  /** The 3D icon in the tile at the top. */
  icon: string;
  buttons: React.ReactNode[];
  onClose: () => void;
  children: React.ReactNode;
};

/**
 * A page-level sheet over the whole screen, tab bar included (boards 1.3,
 * 2.2): a dim that closes it on tap, and a white sheet with a 3D icon tile.
 * Its own buttons live in the sheet, not in the tab bar's slot. Nothing here
 * renders text as HTML: names and messages stay plain text.
 */
export const SuperlikeOverlay = ({
  testId,
  label,
  icon,
  buttons,
  onClose,
  children,
}: SuperlikeOverlayProps) => (
  <div className={styles.overlay} data-test-id={`${testId}-overlay`}>
    <motion.button
      type="button"
      aria-label="Закрыть"
      className={styles.backdrop}
      data-test-id={`${testId}-backdrop`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      onClick={onClose}
    />

    <motion.section
      role="dialog"
      aria-modal="true"
      aria-label={label}
      className={styles.sheet}
      data-test-id={testId}
      initial={{ y: '100%' }}
      animate={{ y: 0 }}
      transition={{ duration: 0.2 }}
    >
      <BottomSheet buttons={buttons} style={{ paddingBottom: 14 }}>
        <div className={styles.tile}>
          <CachedImage
            testId={`${testId}-icon`}
            src={icon}
            alt=""
            blur="2px"
            style={{ width: 80, height: 80 }}
          />
        </div>

        {children}
      </BottomSheet>
    </motion.section>
  </div>
);
